import { useEffect, useRef, useState } from 'react';

type Props<T> = {
  enabled: boolean;
  fetcher: () => Promise<T>;
  getCondition: (data: T) => boolean;
  /**
   * The interval in milliseconds between each fetch.
   */
  interval: number;
  onDone: (data: T) => void;
  onFetch?: (data: T) => void;
};

const usePolling = <T extends {}>(props: Props<T>) => {
  const { fetcher, interval, onDone, onFetch, getCondition } = props;
  const [pollingCount, setPollingCount] = useState(0);
  const intervalRef = useRef<NodeJS.Timer | null>(null);

  const fetchData = async () => {
    try {
      const result = await fetcher();
      setPollingCount((prev) => prev + 1);
      if (getCondition(result)) {
        onDone(result);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      } else {
        onFetch?.(result);
      }
    } catch (error) {
      console.error('Error in usePolling:', error);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  };

  useEffect(() => {
    if (!props.enabled) return;
    intervalRef.current = setInterval(fetchData, interval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [props.enabled]);

  return pollingCount;
};

export default usePolling;
